'use client';

import React, { useState, useCallback } from 'react';
import {
  X,
  Upload,
  FileSpreadsheet,
  AlertCircle,
  Loader2,
  CheckCircle2,
  Search,
} from 'lucide-react';
import { useLazyQuery } from '@apollo/client/react';
import {
  DOCUMENTS_QUERY,
  type Document,
  type DocumentConnection,
  type DocumentFilterInput,
  DocumentType,
  SortField,
  SortDirection,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { Modal } from '@paeffgen/react/crm-documents/ui';
import { formatCurrency, formatDate } from '@paeffgen/react/crm-documents/util';

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ParsedRow {
  documentNumber?: string;
  articleNumber?: string;
  quantity?: number;
  raw: Record<string, string>;
}

/**
 * CSV Import Modal for documents.
 *
 * Parses CSV client-side with Papa Parse (if available) or a simple parser,
 * then searches for matching documents via DOCUMENTS_QUERY.
 *
 * NOTE: There is no "importDocument" mutation in the schema.
 * The import parses the CSV and searches for matching documents by document number.
 * This is a lookup/matching feature, not a data-import feature.
 */
export function ImportModal({ isOpen, onClose }: ImportModalProps) {
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [uploadStatus, setUploadStatus] = useState<
    'IDLE' | 'PARSING' | 'SEARCHING' | 'DONE' | 'ERROR'
  >('IDLE');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [matchedDocuments, setMatchedDocuments] = useState<Document[]>([]);
  const [unmatchedNumbers, setUnmatchedNumbers] = useState<string[]>([]);

  const [searchDocuments] = useLazyQuery<{
    documents: DocumentConnection;
  }>(DOCUMENTS_QUERY, { fetchPolicy: 'network-only' });

  const resetState = useCallback(() => {
    setParsedRows([]);
    setUploadStatus('IDLE');
    setErrorMessage(null);
    setMatchedDocuments([]);
    setUnmatchedNumbers([]);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const parseCsv = (text: string): ParsedRow[] => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (lines.length < 2) return [];

    // Detect separator: semicolon or comma
    const separator = lines[0].includes(';') ? ';' : ',';
    const headers = lines[0].split(separator).map((h) => h.trim().replace(/^["']|["']$/g, ''));

    return lines.slice(1).map((line) => {
      const values = line.split(separator).map((v) => v.trim().replace(/^["']|["']$/g, ''));
      const raw: Record<string, string> = {};
      headers.forEach((h, i) => {
        raw[h] = values[i] || '';
      });

      // Try to find known columns by common names
      const docNumberKey = headers.find((h) =>
        /belegnummer|documentnumber|beleg|document/i.test(h)
      );
      const articleKey = headers.find((h) =>
        /artikelnummer|articlenumber|artikel|article|art/i.test(h)
      );
      const quantityKey = headers.find((h) =>
        /menge|quantity|qty|anzahl/i.test(h)
      );

      return {
        documentNumber: docNumberKey ? raw[docNumberKey] : undefined,
        articleNumber: articleKey ? raw[articleKey] : undefined,
        quantity: quantityKey
          ? parseFloat(raw[quantityKey].replace(',', '.'))
          : undefined,
        raw,
      };
    });
  };

  const handleFile = useCallback(
    async (file: File) => {
      setUploadStatus('PARSING');
      setErrorMessage(null);

      try {
        const text = await file.text();
        const rows = parseCsv(text);

        if (rows.length === 0) {
          setErrorMessage(
            'Die Datei enthält keine gültigen Datenzeilen. Bitte prüfen Sie das Format.'
          );
          setUploadStatus('ERROR');
          return;
        }

        setParsedRows(rows);

        // Extract unique document numbers to search for
        const docNumbers = [
          ...new Set(
            rows
              .map((r) => r.documentNumber)
              .filter((n): n is string => !!n && n.length > 0)
          ),
        ];

        if (docNumbers.length === 0) {
          // No document numbers found - show parsed data anyway
          setUploadStatus('DONE');
          return;
        }

        setUploadStatus('SEARCHING');

        // Search for each document number via the API
        const matched: Document[] = [];
        const unmatched: string[] = [];

        for (const docNum of docNumbers) {
          const filter: DocumentFilterInput = { searchText: docNum };
          const result = await searchDocuments({
            variables: {
              filter,
              first: 5,
              sortBy: SortField.DATE,
              sortDirection: SortDirection.DESC,
            },
          });

          const found = result.data?.documents?.edges
            ?.map((e) => e.node)
            .find(
              (d) =>
                d.documentNumber.toLowerCase() === docNum.toLowerCase()
            );

          if (found) {
            matched.push(found);
          } else {
            unmatched.push(docNum);
          }
        }

        setMatchedDocuments(matched);
        setUnmatchedNumbers(unmatched);
        setUploadStatus('DONE');
      } catch {
        setErrorMessage(
          'Fehler beim Lesen der Datei. Bitte stellen Sie sicher, dass es sich um eine gültige CSV-Datei handelt.'
        );
        setUploadStatus('ERROR');
      }
    },
    [searchDocuments]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Artikelliste importieren">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Artikelliste importieren
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Laden Sie Ihre Bestellliste als Excel oder CSV hoch.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-8 space-y-8">
          {uploadStatus === 'IDLE' && (
            <>
              {/* Template Download */}
              <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="p-3 bg-white rounded-lg shadow-sm text-blue-600 border border-blue-100">
                  <FileSpreadsheet size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Noch keine Vorlage?
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Nutzen Sie unsere Excel-Vorlage für beste Ergebnisse.
                    Einfach Artikelnummer und Menge eintragen.
                  </p>
                </div>
                <a
                  href="/muster_bestellliste.csv"
                  download
                  className="px-4 py-2 bg-white border border-blue-200 text-blue-700 text-sm font-medium rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors shadow-sm whitespace-nowrap"
                >
                  Vorlage herunterladen
                </a>
              </div>

              {/* Upload Zone */}
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className="relative border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center text-center transition-all cursor-pointer group border-gray-300 hover:border-primary hover:bg-gray-50"
              >
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileInput}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />

                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-colors bg-gray-100 text-gray-400 group-hover:bg-orange-100 group-hover:text-primary">
                  <Upload size={32} />
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  <span className="hidden sm:inline">
                    Datei hierhin ziehen
                  </span>
                  <span className="sm:hidden">Datei auswählen</span>
                </h3>
                <p className="text-gray-500 text-sm max-w-sm mx-auto mb-6">
                  oder klicken, um eine Datei von Ihrem Computer auszuwählen.
                  <br />
                  <span className="text-xs text-gray-400 mt-2 block">
                    Unterstützt: .xlsx, .xls, .csv
                  </span>
                </p>
              </div>
            </>
          )}

          {(uploadStatus === 'PARSING' || uploadStatus === 'SEARCHING') && (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 size={48} className="text-primary animate-spin mb-4" />
              <p className="text-lg font-medium text-gray-900">
                {uploadStatus === 'PARSING'
                  ? 'Datei wird analysiert...'
                  : 'Belege werden gesucht...'}
              </p>
              <p className="text-sm text-gray-500">
                {uploadStatus === 'PARSING'
                  ? 'Wir lesen Ihre Datei ein.'
                  : 'Wir matchen Ihre Belegnummern mit der Datenbank.'}
              </p>
            </div>
          )}

          {uploadStatus === 'ERROR' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <AlertCircle size={32} className="text-red-500" />
              </div>
              <p className="text-lg font-medium text-gray-900 mb-2">
                Import fehlgeschlagen
              </p>
              <p className="text-sm text-gray-500 text-center max-w-md">
                {errorMessage}
              </p>
              <button
                onClick={resetState}
                className="mt-6 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover text-sm font-medium transition-colors"
              >
                Erneut versuchen
              </button>
            </div>
          )}

          {uploadStatus === 'DONE' && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle2 size={24} className="text-green-600 shrink-0" />
                <div>
                  <p className="font-medium text-gray-900">
                    {parsedRows.length} Zeilen eingelesen
                  </p>
                  <p className="text-sm text-gray-600">
                    {matchedDocuments.length} Belege gefunden
                    {unmatchedNumbers.length > 0 &&
                      `, ${unmatchedNumbers.length} nicht gefunden`}
                  </p>
                </div>
              </div>

              {/* Matched Documents */}
              {matchedDocuments.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                    <Search size={16} />
                    Gefundene Belege ({matchedDocuments.length})
                  </h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {matchedDocuments.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg"
                      >
                        <div>
                          <span className="font-medium text-gray-900 text-sm">
                            {doc.documentNumber}
                          </span>
                          <span className="text-xs text-gray-500 ml-2">
                            {doc.type}
                          </span>
                        </div>
                        <div className="text-right">
                          {doc.type !== DocumentType.LIEFERSCHEIN && (
                            <span className="text-sm font-medium tabular-nums">
                              {formatCurrency(doc.netTotal ?? 0)}
                            </span>
                          )}
                          <span className="text-xs text-gray-500 ml-2">
                            {formatDate(doc.documentDate)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Unmatched */}
              {unmatchedNumbers.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-orange-700 mb-3 flex items-center gap-2">
                    <AlertCircle size={16} />
                    Nicht gefunden ({unmatchedNumbers.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {unmatchedNumbers.map((num) => (
                      <span
                        key={num}
                        className="px-2.5 py-1 bg-orange-50 text-orange-700 text-sm rounded-md border border-orange-200"
                      >
                        {num}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  onClick={resetState}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Neue Datei
                </button>
                <button
                  onClick={handleClose}
                  className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-hover transition-colors"
                >
                  Schließen
                </button>
              </div>
            </div>
          )}

          {/* Footer Info (only on IDLE) */}
          {uploadStatus === 'IDLE' && (
            <div className="flex items-start gap-3 text-sm text-gray-500 bg-gray-50 p-4 rounded-lg border border-gray-100">
              <AlertCircle
                size={18}
                className="text-primary mt-0.5 shrink-0"
              />
              <p>
                <span className="font-medium text-gray-900">
                  Hinweis zur Prüfung:
                </span>
                <br />
                Wir validieren Ihre Datei sofort nach dem Upload. Falls wir
                Artikel nicht erkennen (z.B. wegen Tippfehlern), können Sie
                diese später als Freitext-Anfrage an Ihren Sachbearbeiter
                senden.
              </p>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
